import { lazy, Suspense, useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';
import Cookies from 'universal-cookie';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import RemoveCookies, { clearCookies } from '../../../../RemoveCookies';
import { redirectToAuthPortalAccount, redirectToAuthPortalLogout } from '../../../../utils/authPortal';
import IconButton from '@mui/material/IconButton';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
// third party
import { FormattedMessage } from 'react-intl';

// project imports
import UpgradePlanCard from './UpgradePlanCard';
import { ThemeMode } from 'config';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import useAuth from 'hooks/useAuth';
import GrantAccessDialog from './GrantAccessDialog';
import AboutDialog from './AboutDialog';
import { OPEN_ABOUT_DIALOG_FROM_SEARCH } from '../SearchSection/searchEvents';
const NotificationSettingsDrawer = lazy(() => import('views/pages/userProfile/notificationSettings'));

// assets
import { IconLogout, IconLifebuoy, IconSettings, IconUser, IconInfoCircle, IconChevronLeft, IconChevronRight, IconBell } from '@tabler/icons-react';
import useConfig from 'hooks/useConfig';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import userProfileIcon from 'assets/images/icons/user-profile.svg';
import TruncateTooltip from '../../../../commonComponent/TruncateTooltip';
import roleManagement from '../../../../utils/roleManagement';
import { isFeatureEnabled } from '../../../../utils/supportedFeatures';
import {
  hasMultipleAccountContexts,
  loadPersistedAccountContexts,
  shouldBlockSwitchToClassic,
  updateGrantedAccountIdsInSnapshot,
} from '../../../../utils/accountContext/accountContextUtils';
import { Button } from '@mui/material';
const ROLE_DISPLAY_NAMES = {
  admin: 'Pro Admin',
  secondaryAdmin: 'Pro Secondary Admin',
  rwManager: 'R/W Manager',
  roManager: 'R/O Manager',
};

function getRoleLabel(roleKey) {
  return ROLE_DISPLAY_NAMES[roleKey] || roleKey || '';
}
// ==============================|| PROFILE MENU ||============================== //

export default function ProfileSection() {
  const theme = useTheme();
  const isMobileHeader = useMediaQuery(theme.breakpoints.down('md'));
  const { mode, borderRadius, showLoader, hideLoader } = useConfig();

  const cookies = new Cookies();

  const [sdm, setSdm] = useState(true);
  const [value, setValue] = useState('');
  const [notification, setNotification] = useState(false);
  const [selectedIndex] = useState(-1);
  const { logout, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [grantAccessModalOpen, setGrantAccessModalOpen] = useState(false);
  const [grantAccessLoading, setGrantAccessLoading] = useState(false);
  const [grantAccessError, setGrantAccessError] = useState('');
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false);
  const [supportSubmenuOpen, setSupportSubmenuOpen] = useState(false);
  const supportMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  // Header AccountContextSection is md+; profile menu covers mobile account switching.
  const showSwitchAccount = isMobileHeader && hasMultipleAccountContexts();
  const hideSwitchToClassic = shouldBlockSwitchToClassic();
  // Support "login as user" stores impersonated email in localStorage (CamSDk loginAsUser); normal
  // login sets cookies. Read both each render so the header never sticks on "User" after impersonation.
  const fromEmailCookie = cookies.get('email') || cookies.get('user_email');
  const profileDisplayEmail =
    fromEmailCookie ||
    (typeof window !== 'undefined' &&
      (window.localStorage.getItem('email') || window.localStorage.getItem('username'))) ||
    'User';
  const isNewLoginPage = location.pathname === '/newLogin' || location.pathname.endsWith('/newLogin');
  // Resolve role/subRole for display and support-access checks; refresh after account switch.
  const [roleState, setRoleState] = useState(() => {
    try {
      return {
        userRole: roleManagement.getUserRole(),
        userSubRole: roleManagement.getUserSubRole(),
      };
    } catch {
      return { userRole: undefined, userSubRole: null };
    }
  });

  useEffect(() => {
    try {
      setRoleState({
        userRole: roleManagement.getUserRole(),
        userSubRole: roleManagement.getUserSubRole(),
      });
    } catch {
      setRoleState({ userRole: undefined, userSubRole: null });
    }
  }, [location.pathname, location.state?.accountSwitched]);

  const userRoleForSupportAccess = roleState.userRole;
  const userSubRoleForSupportAccess = roleState.userSubRole;

  const [supportAccessGranted, setSupportAccessGranted] = useState(() => cookies.get('supportAccessSt') === '1');
  useEffect(() => {
    const onGranted = () => setSupportAccessGranted(true);
    const onRevoked = () => setSupportAccessGranted(false);
    window.addEventListener('supportAccessGranted', onGranted);
    window.addEventListener('supportAccessRevoked', onRevoked);
    return () => {
      window.removeEventListener('supportAccessGranted', onGranted);
      window.removeEventListener('supportAccessRevoked', onRevoked);
    };
  }, []);

  // Global search "About" result opens the same dialog as the profile menu item.
  useEffect(() => {
    const onOpenAbout = () => {
      setOpen(false);
      setAboutDialogOpen(true);
    };
    window.addEventListener(OPEN_ABOUT_DIALOG_FROM_SEARCH, onOpenAbout);
    return () => window.removeEventListener(OPEN_ABOUT_DIALOG_FROM_SEARCH, onOpenAbout);
  }, []);

  // Grant Access gate:
  //   canGrantSupportAccessFromRoles(role, subRole) && not a support session
  // Read-only managers are allowed (TSM grant is consent, not org write).
  // Flag-on stamps `roManager`; hiding Grant for that role made flag-on/off differ.
  const canGrantSupportAccess = roleManagement.canGrantSupportAccessFromRoles(userRoleForSupportAccess, userSubRoleForSupportAccess);
  const supportAccessLevelUserAccessLevel = (typeof localStorage !== 'undefined') ? localStorage.getItem('accessLevel') : null;
  const isSupportAccessLevelUser = supportAccessLevelUserAccessLevel === 's1' || supportAccessLevelUserAccessLevel === 's2' || supportAccessLevelUserAccessLevel === 's3';

  // Feature gate for the Notification Settings menu entry — hidden unless
  // feature id "303" has status "1" in the cached `featureSupportAdminCheck`
  // payload. Uses the shared `isFeatureEnabled` helper (try/catch + cache
  // + invalidation hook) instead of an inline JSON.parse so a malformed
  // localStorage value can't crash ProfileSection.
  const notificationSettingsFeatureEnabled = isFeatureEnabled('303');

  // Role gate for the Notification Settings menu entry. Mirrors the legacy
  // `accMngLeftMenu.js` rule where "Manage Notification Preferences" was
  // shown only to a limited set of roles. Roles such as roManager, owner,
  // voucherAdmin, preVoucherAdmin, supportAgent and lineManager remain hidden.
  // Support login-as sessions (userRole = impersonated TSM/admin,
  // userSubRole = supportAgent) may open the drawer read-only — see
  // notificationSettingsAccess.js.
  const NOTIFICATION_SETTINGS_ALLOWED_ROLES = ['admin', 'secondaryAdmin', 'rwManager', 'generic'];
  const isRoleAllowedForNotificationSettings = (roleKey) => NOTIFICATION_SETTINGS_ALLOWED_ROLES.indexOf(roleKey) >= 0;
  const notificationSettingsRoleAllowed =
    isRoleAllowedForNotificationSettings(userRoleForSupportAccess) &&
    (!userSubRoleForSupportAccess ||
      isRoleAllowedForNotificationSettings(userSubRoleForSupportAccess) ||
      userSubRoleForSupportAccess === 'supportAgent');

  const notificationSettingsEnabled = notificationSettingsFeatureEnabled && notificationSettingsRoleAllowed;

  /**
    anchorRef is used on different components and specifying one type leads to other components throwing an error
   * */

  const anchorRef = useRef(null);

  /**
   * Logout: (1) Call Insight logout API and clear app cookies, (2) Redirect to OC auth portal logout.
   * OC clears all cookies and redirects user back to our website (per OC team).
   */
  const handleLogout = () => {
    showLoader();
    if (userSubRoleForSupportAccess === 'supportAgent') {
      const redirectToSupportHome = () => {
        hideLoader();
        window.location.replace('/');
      };

      RemoveCookies.exitSupportUserSession()
        .then(redirectToSupportHome)
        .catch((err) => {
          redirectToSupportHome();
        });
      return;
    }

    RemoveCookies.removeAccCookies()
      .then(() => {
        hideLoader();
        redirectToAuthPortalLogout();
      })
      .catch((err) => {
        console.error('Error during logout:', err);
        hideLoader();
        redirectToAuthPortalLogout();
      });
  };

  const handleUpdateProfile = useCallback(() => {
    setOpen(false);
    redirectToAuthPortalAccount();
  }, []);

  const handleSwitchAccount = useCallback(() => {
    setOpen(false);
    navigate('/account-selection', {
      state: {
        contexts: loadPersistedAccountContexts(),
        switchMode: true,
      },
    });
  }, [navigate]);

  const handleNotificationSettings = useCallback(() => {
    setOpen(false);
    setNotificationSettingsOpen(true);
  }, []);

  const handleSupportSubmenuToggle = useCallback(() => {
    setSupportSubmenuOpen((prev) => !prev);
  }, []);

  const handleMyRegisteredProductClick = useCallback(() => {
    const supportUrl = process.env.supportUrl;
    if (supportUrl) {
      window.open(supportUrl, '_blank', 'noopener,noreferrer');
    }
    setSupportSubmenuOpen(false);
    setOpen(false);
  }, []);

  const handleGrantAccessClick = useCallback(() => {
    if (!canGrantSupportAccess || supportAccessGranted) return;
    setSupportSubmenuOpen(false);
    setOpen(false);
    setGrantAccessError('');
    setGrantAccessModalOpen(true);
  }, [canGrantSupportAccess, supportAccessGranted]);

  const handleGrantAccessConfirm = useCallback(async () => {
    setGrantAccessLoading(true);
    setGrantAccessError('');

    try {
      const { default: useApi } = await import('../../../../hooks/useApi');
      const { getErrMessage } = await import('../../../../utils/CommonMethods');
      const cookies = new Cookies();
      const customerId = cookies.get('userid') || cookies.get('userId'); // Get customerId from userId cookie
      const secureCookie = process.env.secureCoockie || true;

      const data = await useApi(
        'GRANT_SUPPORT_ACCESS',
        {},
        {
          data: {
            suppAccessSt: "1",
            customerId: customerId,
            accountId: cookies.get('accountId')
          }
        },
        []
      );

      // Success if status is true OR responseCode is "44005"
      if (data?.response?.status || data?.response?.responseCode === "44005") {
        // Set cookie
        cookies.set('supportAccessSt', "1", { secure: secureCookie, path: '/' });
        updateGrantedAccountIdsInSnapshot(cookies.get('accountId'), true);

        // Update local state immediately so this menu re-renders without
        // waiting for the round-trip event from MainLayout.
        setSupportAccessGranted(true);

        // Notify parent component (MainLayout) to show the banner.
        if (window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('supportAccessGranted'));
        }
        setGrantAccessError('');
        setGrantAccessModalOpen(false);
      } else {
        // Handle error — keep dialog open and show message
        const responseCode = data?.response?.responseCode;
        const errorMsg = responseCode ? <FormattedMessage id={responseCode} /> : data?.response?.message || 'Error granting access';
        setGrantAccessError(errorMsg);
      }
    } catch (error) {
      console.error('Error granting support access:', error);
      const fallback =
        error?.response?.data?.message ||
        error?.message ||
        'Error granting support access';
      setGrantAccessError(fallback);
    } finally {
      setGrantAccessLoading(false);
    }
  }, []);

  const handleGrantAccessClearError = useCallback(() => {
    setGrantAccessError('');
  }, []);

  const handleGrantAccessClose = useCallback(() => {
    setGrantAccessError('');
    setGrantAccessModalOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const handleSwitchToClassic = useCallback(() => {
    if (shouldBlockSwitchToClassic()) {
      setOpen(false);
      return;
    }
    setOpen(false);
    const redirectUrl = process.env.camSdkRedirectUrl || window.location.origin + '/';
    const baseUrl = String(redirectUrl).replace(/\/mui\/?/i, '').replace(/\/+$/, '');
    const classicUrl = `${baseUrl}/classic`;
    let classicWindow = null;
    try {
      classicWindow = window.open('', 'ng-insight-classic');
      if (classicWindow && !classicWindow.closed) {
        classicWindow.location.href = classicUrl;
        classicWindow.focus();
        return;
      }
    } catch (e) {
      // Fallback to opening a new named window
    }
    window.open(classicUrl, 'ng-insight-classic');
  }, []);

  const handleClose = useCallback((event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    if (supportMenuRef.current && supportMenuRef.current.contains(event.target)) {
      return;
    }
    const submenuEl = document.getElementById('support-submenu-paper');
    if (submenuEl && submenuEl.contains(event.target)) {
      return;
    }

    setSupportSubmenuOpen(false);
    setOpen(false);
  }, []);

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
      setSupportSubmenuOpen(false);
    }

    prevOpen.current = open;
  }, [open]);


  return (
    <>
      <Chip
        id="profile-section-trigger-chip"
        sx={{
          ml: 1,
          alignItems: 'center',
          borderRadius: '27px',
          backgroundColor:'transparent',
          
          '& .MuiChip-label': {
            lineHeight: 0,
            padding: 0,
          },
          '&.MuiChip-clickable:hover': {
            backgroundColor: 'transparent',
          }
        }}
        label={<IconButton
            id="profile-section-avatar-button"
            sx={{
              color: 'white.paper',
              padding: .7,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {/* <AccountCircleIcon stroke={1} sx={{fontSize:'24px'}} /> */}
            <Box component='img' src={userProfileIcon} alt="User Profile" width={26} height={26} id="profile-section-avatar-image" />
          </IconButton>
          }
        ref={anchorRef}
        aria-controls={open ? 'profile-section-menu-list' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        aria-label="user-account"
      />
      <Popper
        id="profile-section-popper"
        placement="bottom"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 14]
            }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper id="profile-section-dropdown-paper">
                {open && (
                  <MainCard id="profile-section-dropdown-card" border={true} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                    <Box id="profile-section-dropdown-content" sx={{ p: 2,  maxWidth: '250px' }}> {/* pb: 1, */}
                      
                      <Box 
                        id="profile-section-user-info"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems:'center',
                          justifyContent: 'center',
                          mb: 2
                        }}
                      >
                        <AccountCircleOutlinedIcon
                          id="profile-section-account-icon"
                          sx={{
                            width: '42px',
                            height: '42px',
                            background: theme.palette.bluegrey.light,
                            borderRadius: 1.25,
                            p: 1,
                            color: 'teal.500'
                          }}
                        ></AccountCircleOutlinedIcon>
                        <TruncateTooltip id="profile-section-email" variant='body1' color='grey.600' sx={{mt: 1, maxWidth: '100%'}}>{ profileDisplayEmail }</TruncateTooltip>
                        {/* <Typography variant='subtitle2' color='grey.500'>Super Admin</Typography> */} {/* Intentionaly commented we need it later.*/}
                        {userRoleForSupportAccess && (
                          <Typography id="profile-section-role" variant='subtitle2' color='grey.500'>{getRoleLabel(userRoleForSupportAccess)}</Typography>
                        )}
                      </Box>

                     
                      <Divider id="profile-section-divider-after-email" sx={{borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[150]}}/>
                      <List
                        id="profile-section-menu-list"
                        component="nav"
                        sx={{
                          width: '100%',
                          maxWidth: 250,
                          minWidth: 218,
                          borderRadius: `${borderRadius}px`,
                          p: 0,
                          '& .MuiListItemButton-root': { mt: 0 }
                        }}
                      >
                      <Divider id="profile-section-divider-after-settings" sx={{borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[150]}}/>
                        {!isNewLoginPage && (
                          <>
                            {showSwitchAccount && (
                              <>
                                <ListItemButton
                                  id="profile-section-menu-item-switch-account"
                                  sx={{ borderRadius: `${borderRadius}px`, p: 1.25 }}
                                  onClick={handleSwitchAccount}
                                >
                                  <ListItemIcon id="profile-section-menu-item-switch-account-icon">
                                    <SwapHorizIcon sx={{ fontSize: 20 }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    id="profile-section-menu-item-switch-account-text"
                                    primary={
                                      <Typography variant="body1" color="text.primary">
                                        <FormattedMessage id="switchAccount" defaultMessage="Switch Account" />
                                      </Typography>
                                    }
                                  />
                                </ListItemButton>
                                <Divider id="profile-section-divider-after-switch-account" sx={{ borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[150] }} />
                              </>
                            )}
                            <ListItemButton id="profile-section-menu-item-update-profile" sx={{ borderRadius: `${borderRadius}px`, p: 1.25 }} selected={selectedIndex === 5} onClick={() => handleUpdateProfile()}>
                              <ListItemIcon id="profile-section-menu-item-update-profile-icon">
                                <IconUser stroke={1.5} size="20px" />
                              </ListItemIcon>
                              <ListItemText
                                id="profile-section-menu-item-update-profile-text"
                                primary={
                                  <Grid container spacing={1} sx={{ justifyContent: 'space-between' }} id="profile-section-update-profile-grid">
                                    <Grid id="profile-section-update-profile-grid-label">
                                      <Typography id="profile-section-update-profile-label" variant="body1" color='text.primary'>
                                        <FormattedMessage id="updateProfile" />
                                      </Typography>
                                    </Grid>
                                    {/* <Grid>
                                  <Chip
                                    label="02"
                                    variant="filled"
                                    size="small"
                                    color="warning"
                                    sx={{ '& .MuiChip-label': { mt: 0.25, ...theme.applyStyles('dark', { color: 'background.default' }) } }}
                                  />
                                </Grid> */}
                                  </Grid>
                                }
                              />
                            </ListItemButton>
                            <Divider id="profile-section-divider-after-update-profile" sx={{ borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[150] }} />
                            {notificationSettingsEnabled && (
                              <>
                                <ListItemButton
                                  id="profile-section-menu-item-notification-settings"
                                  sx={{ borderRadius: `${borderRadius}px`, p: 1.25 }}
                                  onClick={handleNotificationSettings}
                                >
                                  <ListItemIcon id="profile-section-menu-item-notification-settings-icon">
                                    <IconBell stroke={1.5} size="20px" />
                                  </ListItemIcon>
                                  <ListItemText
                                    id="profile-section-menu-item-notification-settings-text"
                                    primary={
                                      <Typography id="profile-section-menu-item-notification-settings-label" variant="body1" color='text.primary'>
                                        <FormattedMessage id="notificationSettings" />
                                      </Typography>
                                    }
                                  />
                                </ListItemButton>
                                <Divider id="profile-section-divider-after-notification-settings" sx={{ borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[150] }} />
                              </>
                            )}
                            <Box ref={supportMenuRef}>
                              <ListItemButton
                                id="profile-section-menu-item-support"
                                onClick={handleSupportSubmenuToggle}
                                sx={{
                                  borderRadius: `${borderRadius}px`,
                                  p: 1.25,
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                                  }
                                }}
                              >
                                <ListItemIcon id="profile-section-menu-item-support-icon">
                                  <IconLifebuoy stroke={1.25} size={20} />
                                </ListItemIcon>
                                <ListItemText
                                  id="profile-section-menu-item-support-text"
                                  primary={
                                    <Typography id="profile-section-menu-item-support-label" variant="body1" color='text.primary'>
                                      <FormattedMessage id="support" />
                                    </Typography>
                                  }
                                />
                                <IconChevronRight size={16} stroke={1.5} />
                              </ListItemButton>
                            </Box>
                            <Divider id="profile-section-divider-after-support" sx={{ borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[150] }} />
                            <ListItemButton id="profile-section-menu-item-about" sx={{ borderRadius: `${borderRadius}px`, p: 1.25 }} onClick={() => { setOpen(false); setAboutDialogOpen(true); }}>
                              <ListItemIcon id="profile-section-menu-item-about-icon">
                                <IconInfoCircle stroke={1.5} size="20px" />
                              </ListItemIcon>
                              <ListItemText
                                id="profile-section-menu-item-about-text"
                                primary={
                                  <Typography id="profile-section-menu-item-about-label" variant="body1" color='text.primary'>
                                    <FormattedMessage id="about" />
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                            <Divider id="profile-section-divider-after-about" sx={{ borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[150] }} />
                          </>
                        )}
                        <ListItemButton id="profile-section-menu-item-logout" sx={{ borderRadius: `${borderRadius}px`, p: 1.25 }} selected={selectedIndex === 4} onClick={handleLogout}>
                          <ListItemIcon id="profile-section-menu-item-logout-icon">
                            <IconLogout stroke={1.5} size="20px" />
                          </ListItemIcon>
                          <ListItemText
                            id="profile-section-menu-item-logout-text"
                            primary={
                              <Typography id="profile-section-menu-item-logout-label" variant="body1" color='text.primary'>
                                <FormattedMessage id="logout" />
                              </Typography>
                            }
                          />
                        </ListItemButton>
                        {!isNewLoginPage && !hideSwitchToClassic && (
                          <>
                            <Divider id="profile-section-divider-after-logout" sx={{ borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[150] }} />
                            <Button
                              variant="outlined"
                              fullWidth
                              size='small'
                              startIcon={<IconChevronLeft size={16} />}
                              onClick={handleSwitchToClassic}
                              id="profile-section-menu-item-switch-to-classic"
                              sx={{ 
                                mt: 2,
                                whiteSpace: 'nowrap',
                                '& .MuiButton-startIcon': {
                                  marginRight: 0.5,
                                },

                               }}
                            >
                              <FormattedMessage id="switchToClassic" />
                            </Button>
                          </>
                        )}
                      </List>
                    </Box>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
      
      {/* Grant Access Modal */}
      <GrantAccessDialog
        open={grantAccessModalOpen}
        onClose={handleGrantAccessClose}
        onConfirm={handleGrantAccessConfirm}
        loading={grantAccessLoading}
        errorMessage={grantAccessError}
        onClearError={handleGrantAccessClearError}
      />

      {/* About Dialog */}
      <AboutDialog
        open={aboutDialogOpen}
        onClose={() => setAboutDialogOpen(false)}
      />

      {/* Support Submenu Flyout */}
      {supportSubmenuOpen && open && (
        <Popper
          id="support-submenu-popper"
          open
          anchorEl={supportMenuRef.current}
          placement="left"
          disablePortal={false}
          sx={{ zIndex: 1400 }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 10]
              }
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 8
              }
            }
          ]}
        >
          <Box sx={{ position: 'relative', mr: 0.5 }}>
            {/* Triangle arrow pointing right toward the Support menu item */}
            <Box
              sx={{
                position: 'absolute',
                right: -5,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '7px solid transparent',
                borderBottom: '7px solid transparent',
                borderLeft: `7px solid ${theme.palette.mode === 'dark' ? theme.palette.dark?.[800] : theme.palette.background.paper}`,
                zIndex: 1
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                right: -6,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '7px solid transparent',
                borderBottom: '7px solid transparent',
                borderLeft: `7px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[200]}`,
                zIndex: 0
              }}
            />
            <Paper
              id="support-submenu-paper"
              sx={{
                minWidth: 200,
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark?.[800] : theme.palette.background.paper,
                borderRadius: `${borderRadius}px`,
                boxShadow: theme.shadows[16],
                border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[200]}`,
                py: 0.5
              }}
            >
              <List component="nav" sx={{ p: 0 }}>
                {/* Technical Support — mirrors classic Header.js:
                      checkUser("TechSupport") && supportAccessLevel.indexOf(userAccessLevel) < 0
                    Hide when role/subRole disallows TechSupport, when the user is a
                    support session (accessLevel s1/s2/s3), or when access is already granted. */}
                {canGrantSupportAccess && !isSupportAccessLevelUser && !supportAccessGranted && (
                  <ListItemButton
                    id="support-submenu-grant-access"
                    onClick={handleGrantAccessClick}
                    sx={{
                      px: 2,
                      py: 1.25,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body1" color="text.primary" sx={{ fontSize: '0.875rem' }}>
                          <FormattedMessage id="grantAccessToSupport" />
                        </Typography>
                      }
                    />
                  </ListItemButton>
                )}
                <ListItemButton
                  id="support-submenu-my-registered-product"
                  onClick={handleMyRegisteredProductClick}
                  sx={{
                    px: 2,
                    py: 1.25,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body1" color="text.primary" sx={{ fontSize: '0.875rem' }}>
                        <FormattedMessage id="getTechnicalSupport" />
                      </Typography>
                    }
                  />
                </ListItemButton>
              </List>
            </Paper>
          </Box>
        </Popper>
      )}

      <Suspense fallback={null}>
        {notificationSettingsOpen && (
          <NotificationSettingsDrawer
            open={notificationSettingsOpen}
            onClose={() => setNotificationSettingsOpen(false)}
          />
        )}
      </Suspense>
    </>
  );
}
